/*
 * Hub Onboarding — animated scenes (Feb 2026).
 *
 * Each scene is a pure presentational component that receives the
 * `animate` prop from the orchestrator so it only starts moving when
 * it is actually visible. `reducedMotion` swaps continuous motion for
 * a plain cross-fade to respect `prefers-reduced-motion`.
 *
 * NONE of these scenes trigger a real Hub action. Spin/Submit/Yes/No
 * clicks are decorative — every backend integration stays untouched.
 */
import { useEffect, useState } from "react";
import { FragmentDiamond } from "./FragmentDiamond";

/* ────────────────────────────────────────────────────────────────
 * 1A · Daily Spin
 * ──────────────────────────────────────────────────────────────── */
export function SceneDailySpin({ animate, reducedMotion }) {
  // Wheel demo: mount stationary, wait ~280ms so the entrance can
  // settle, then spin for exactly 2s and land on the "5 FRG" slice
  // (the right sector — rotated so it sits under the top pointer).
  // A short glow on the winning slice confirms the result. SPIN NOW
  // bumps `spinKey`, replaying the same 2s demonstration.
  const [spinKey, setSpinKey] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [landed, setLanded] = useState(false);

  useEffect(() => {
    setSpinning(false);
    setLanded(false);
    if (!animate) return;
    if (reducedMotion) { setSpinning(true); setLanded(true); return; }
    const startT = setTimeout(() => setSpinning(true), 280);
    const landT  = setTimeout(() => setLanded(true), 280 + 2000);
    return () => { clearTimeout(startT); clearTimeout(landT); };
  }, [spinKey, animate, reducedMotion]);

  // 4 full turns + 270° so the right slice (5 FRG) sits under the
  // pointer at rest. Kept in a CSS variable so a single transition
  // declaration handles both start (rotate 0) and end.
  const rotation = reducedMotion ? 270 : 1710;

  return (
    <div className="hbo-scene hbo-scene--spin">
      <div className="hbo-spin-hero" aria-hidden="true">
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--white">
          SPIN DAILY.
        </h2>
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--tint">
          EARN FRAGMENTS.
        </h2>
        <span className="hbo-spin-hero-sweep" aria-hidden="true" />
      </div>
      <div className="hbo-spin-side">
        <div className="hbo-spin-label">DAILY SPIN</div>
        <div className="hbo-spin-pointer" aria-hidden="true" />
        <div
          key={spinKey}
          className={`hbo-wheel ${spinning ? "is-spinning" : ""} ${landed ? "is-landed" : ""}`}
          style={{ "--hbo-rot": `${rotation}deg` }}
          data-testid="hbo-wheel"
        >
          {/* Sectors: Top=1 FRG, Right=5 FRG (winning), Bottom=10, Left=2. */}
          <div className="hbo-wheel-slice hbo-wheel-slice--top" data-testid="hbo-spin-slice-top">
            <FragmentDiamond size={22} />
            <div className="hbo-wheel-slice-value"><b>1</b><span>FRG</span></div>
          </div>
          <div className="hbo-wheel-slice hbo-wheel-slice--right" data-testid="hbo-spin-slice-right">
            <FragmentDiamond size={22} />
            <div className="hbo-wheel-slice-value hbo-wheel-slice-value--green"><b>5</b><span>FRG</span></div>
          </div>
          <div className="hbo-wheel-slice hbo-wheel-slice--bottom">
            <FragmentDiamond size={22} />
            <div className="hbo-wheel-slice-value"><b>10</b><span>FRG</span></div>
          </div>
          <div className="hbo-wheel-slice hbo-wheel-slice--left">
            <FragmentDiamond size={22} />
            <div className="hbo-wheel-slice-value hbo-wheel-slice-value--blue"><b>2</b><span>FRG</span></div>
          </div>
          <div className="hbo-wheel-hub" />
        </div>
        <button
          type="button"
          className="hbo-cta hbo-cta--wheel"
          onClick={() => setSpinKey((k) => k + 1)}
          data-testid="hbo-spin-now-btn"
        >
          SPIN NOW
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 1B · Share community posts
 * ──────────────────────────────────────────────────────────────── */
const SHARE_POSTS = [
  // Front on entry — matches the attached preview.
  {
    key: "mehrdad",
    handle: "Mehrdad [🖤]",
    at: "@Mehrdad643…",
    body: "@ProphetsOnSol first season of THE SWEEP is finished\nBUY. ENTER. WIN.\nCongrats to winners",
    img: "/img/hub/onboarding/mehrdad-sweep-final-prizes.webp",
    date: "3:34 PM · Aug 24, 2026",
  },
  {
    key: "maximilian",
    handle: "maximilian 🥀",
    at: "@anghelm82",
    body: "@ProphetsOnSol 10 families. One prize. One jackpot 50sol.\n\nFAMILY RACE is a community competition powered by predictions, strategy, and weekly Discord events. Each family must work together to reach the finish line before the others.\n\n…it's just the beginning",
    img: "/img/games/family-race-banner.webp",
  },
  {
    key: "helena",
    handle: "helena",
    at: "@Ciciy_89",
    body: "Calling the Singularity faction! 🌌🔮\nThe Family Prediction dashboard is now open for the US Open match between Sebastian Ofner and Kei Nishikori!\nLock in your choices within the next 21 hours to contribute crucial points on our faction's race track! 🎾\n@ProphetsOnSol",
    img: "/img/hub/onboarding/helena-singularity-usopen.webp",
  },
];

export function SceneShare({ animate, reducedMotion }) {
  // Carousel cycles Mehrdad → maximilian → helena → repeat. Front
  // card stays legible for ~2.5s, then the deck rotates. Reduced
  // motion → hold the first card static so nothing moves.
  const [front, setFront] = useState(0);
  useEffect(() => {
    if (!animate || reducedMotion) { setFront(0); return; }
    setFront(0);
    const id = setInterval(() => setFront((i) => (i + 1) % SHARE_POSTS.length), 2500);
    return () => clearInterval(id);
  }, [animate, reducedMotion]);

  const leftIdx  = (front + SHARE_POSTS.length - 1) % SHARE_POSTS.length;
  const rightIdx = (front + 1) % SHARE_POSTS.length;

  return (
    <div className="hbo-scene hbo-scene--share">
      <div className="hbo-share-hero" aria-hidden="true">
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--white">
          SHARE POSTS.
        </h2>
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--tint">
          EARN FRAGMENTS.
        </h2>
        <span className="hbo-spin-hero-sweep" aria-hidden="true" />
      </div>
      <div className="hbo-share-deck" data-testid="hbo-share-deck">
        <XPostCard post={SHARE_POSTS[leftIdx]}  slot="left"  />
        <XPostCard post={SHARE_POSTS[front]}    slot="front" />
        <XPostCard post={SHARE_POSTS[rightIdx]} slot="right" />
      </div>
    </div>
  );
}

function XPostCard({ post, slot }) {
  return (
    <article className={`hbo-xpost hbo-xpost--${slot}`}
             data-testid={`hbo-xpost-${slot}`}>
      <div className="hbo-xpost-header">
        <span className="hbo-xpost-badge">
          <XLogo /> X POST
        </span>
        <XLogo className="hbo-xpost-logo" />
      </div>
      <div className="hbo-xpost-author">
        <div className="hbo-xpost-avatar" aria-hidden="true">
          {(post.handle || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="hbo-xpost-author-meta">
          <div className="hbo-xpost-name">{post.handle}</div>
          <div className="hbo-xpost-at">{post.at}<span> · </span><b>Follow</b></div>
        </div>
      </div>
      <p className="hbo-xpost-body">{post.body}</p>
      {post.img && (
        <div className="hbo-xpost-img"
             role="img"
             aria-label={`${post.handle} post image`}
             style={{ backgroundImage: `url("${post.img}")` }} />
      )}
      {post.date && <div className="hbo-xpost-date">{post.date}</div>}
    </article>
  );
}

function XLogo({ className }) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14"
         className={className} aria-hidden="true">
      <path fill="currentColor" d="M18.244 2H21l-6.53 7.463L22 22h-6.828l-4.75-6.213L4.8 22H2l7.03-8.037L2 2h6.914l4.294 5.68L18.244 2Zm-1.196 18.09h1.62L7.03 3.83H5.32L17.048 20.09Z"/>
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 2 · Play & Win (Knox Smash)
 * ──────────────────────────────────────────────────────────────── */
export function ScenePlay({ animate, reducedMotion }) {
  // Ambient combat cycle: Knox idles (~2s) → lunges into an impact
  // pose while the wall shakes and swaps to the damaged variant →
  // resets. Cycle repeats while the scene is visible. Purely visual.
  const [phase, setPhase] = useState("idle"); // "idle" | "impact"
  useEffect(() => {
    if (!animate || reducedMotion) { setPhase("idle"); return; }
    let cancelled = false;
    let t;
    const step = (next, wait) => {
      t = setTimeout(() => {
        if (cancelled) return;
        setPhase(next);
        step(next === "idle" ? "impact" : "idle", next === "idle" ? 2400 : 700);
      }, wait);
    };
    setPhase("idle");
    step("impact", 2400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [animate, reducedMotion]);

  const knoxImg = phase === "impact"
    ? "/img/games/knox-smash/knox-smash-impact-transparent.png"
    : "/img/games/knox-smash/knox-smash-ready-transparent.png";
  const wallImg = phase === "impact"
    ? "/img/games/knox-smash/steel-wall-damaged.png"
    : "/img/games/knox-smash/steel-wall-intact.png";

  return (
    <div className="hbo-scene hbo-scene--play">
      <div className="hbo-share-hero" aria-hidden="true">
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--white">
          PLAY &amp; WIN.
        </h2>
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--tint">
          RISK YOUR FRAGMENTS.
        </h2>
        <span className="hbo-spin-hero-sweep" aria-hidden="true" />
      </div>
      <div className="hbo-play-frame" data-testid="hbo-play-frame">
        <div className="hbo-play-frame-title">KNOX SMASH</div>
        <div className={`hbo-play-frame-stage is-${phase}`}>
          <img src={knoxImg}
               alt=""
               className="hbo-play-frame-knox"
               data-testid="hbo-play-knox-img" />
          <img src={wallImg}
               alt=""
               className="hbo-play-frame-wall"
               data-testid="hbo-play-wall-img" />
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 3A · Predict the future
 * ──────────────────────────────────────────────────────────────── */
const PREDICT_STAGES = [
  {
    key: "crypto",
    tab: "CRYPTO",
    chip: "CRYPTO",
    tone: "cyan",
    question: "Will the price of Bitcoin be above $80,000 on August 25?",
    close: "2026-08-25 01:00 UTC",
    left: "YES",
    right: "NO",
    reward: "+8 FRG",
  },
  {
    key: "polymarket",
    tab: "POLYMARKET",
    chip: "POLYMARKET",
    tone: "violet",
    question: "Málaga CF vs. RC Deportivo A Coruña: Both Teams to Score",
    close: "2026-08-24 18:00 UTC",
    left: "YES",
    right: "NO",
    reward: "+8 FRG",
  },
  {
    key: "nft",
    tab: "NFT",
    chip: "NFT",
    tone: "pink",
    question: "Will the Prophets floor be HIGHER than 0.5 SOL in 24h?",
    close: "2026-08-25 20:00 UTC",
    left: "HIGHER",
    right: "LOWER",
    reward: "+8 FRG",
  },
];

export function ScenePredict({ animate, reducedMotion }) {
  // Auto-sequence: Crypto (0) → Polymarket (1) → NFT (2) — then stop.
  // Each card enters from the right and exits to the left; the tab
  // highlight slides in sync. All timers are cleaned on unmount so
  // the sequence cannot continue in the background.
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!animate) { setIdx(0); return; }
    if (reducedMotion) { setIdx(PREDICT_STAGES.length - 1); return; }
    setIdx(0);
    const t1 = setTimeout(() => setIdx(1), 1800);
    const t2 = setTimeout(() => setIdx(2), 3800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [animate, reducedMotion]);

  const stage = PREDICT_STAGES[idx];

  return (
    <div className="hbo-scene hbo-scene--predict">
      <div className="hbo-share-hero" aria-hidden="true">
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--white">PREDICT THE</h2>
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--white">FUTURE.</h2>
        <h2 className="hbo-spin-hero-line hbo-spin-hero-line--tint">EARN FRAGMENTS.</h2>
        <span className="hbo-spin-hero-sweep" aria-hidden="true" />
      </div>
      <div className="hbo-predict-side" data-testid="hbo-predict-side">
        <div className="hbo-predict-tabs" role="tablist" data-testid="hbo-predict-tabs">
          {PREDICT_STAGES.map((s, i) => (
            <button key={s.key} type="button" tabIndex={-1}
                    className={`hbo-predict-tab hbo-predict-tab--${s.tone} ${i === idx ? "is-active" : ""}`}
                    data-testid={`hbo-predict-tab-${s.key}`}>
              {s.tab}
            </button>
          ))}
        </div>
        <div className="hbo-predict-card-stack" data-testid="hbo-predict-card-stack">
          {PREDICT_STAGES.map((s, i) => {
            const rel = i - idx;
            const slot = rel < 0 ? "out-left" : rel > 0 ? "queued" : "active";
            return (
              <article key={s.key}
                       className={`hbo-predict-card2 hbo-predict-card2--${s.tone} is-${slot}`}
                       data-testid={`hbo-predict-card-${s.key}`}
                       aria-hidden={slot !== "active"}>
                <div className="hbo-predict-card2-head">
                  <span className={`hbo-predict-cat2 hbo-predict-cat2--${s.tone}`}>{s.chip}</span>
                  <span className={`hbo-predict-award2 hbo-predict-award2--${s.tone}`}>{s.reward}</span>
                </div>
                <div className="hbo-predict-card2-q">{s.question}</div>
                <div className="hbo-predict-card2-meta">
                  <span>Voting closes: <b>{s.close}</b></span>
                  <span>Reward: <b className="hbo-predict-card2-reward">{s.reward}</b></span>
                </div>
                <div className="hbo-predict-card2-vote">
                  <button type="button" tabIndex={-1}
                          className={`hbo-predict-card2-btn hbo-predict-card2-btn--yes hbo-predict-card2-btn--${s.tone}`}>
                    {s.left}
                  </button>
                  <button type="button" tabIndex={-1}
                          className={`hbo-predict-card2-btn hbo-predict-card2-btn--no hbo-predict-card2-btn--${s.tone}`}>
                    {s.right}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 3B · Climb the leaderboard
 * ────────────────────────────────────────────────────────────────
 * Starting state (reference 1):     Final state (reference 2):
 *   #1 Tziba       69.23% 26 18·8    #1 Tziba       69.23% 26 18·8
 *   #2 Helena      53.57% 28 15·13   #2 Helena      53.57% 28 15·13
 *   #3 LeLe FAM100 50%    30 15·15   #3 YOU         52%    42 22·20
 *   #4 Adrian      45.83% 24 11·13   #4 LeLe FAM100 50%    30 15·15
 *   #5 YOU         42.11% 38 16·22   #5 Adrian      45.83% 24 11·13
 *
 * DOM row order is stable (Tziba, Helena, LeLe, Adrian, YOU); the
 * visual slot is driven by the `--row-slot` CSS variable so rows
 * glide instead of re-mounting. YOU's Resolved / Accuracy / C-I
 * numbers tween via rAF while the CSS transform handles the slide. */
const LB_YOU_START = { slot: 4, rank: 5, acc: 42.11, res: 38, correct: 16, incorrect: 22 };
const LB_YOU_FINAL = { slot: 2, rank: 3, acc: 52,    res: 42, correct: 22, incorrect: 20 };

function lerp(a, b, t) { return a + (b - a) * t; }
function easeInOut(t)  { return t < 0.5 ? 2*t*t : 1 - Math.pow(-2*t + 2, 2)/2; }

export function SceneLeaderboard({ animate, reducedMotion }) {
  // `progress` (0..1) drives every animated value on this scene:
  //   • YOU stats (Accuracy / Resolved / C-I)
  //   • right-panel #5 → #3
  //   • CSS transform slots for YOU, LeLe FAM100 and Adrian
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!animate)      { setProgress(0); return; }
    if (reducedMotion) { setProgress(1); return; }
    setProgress(0);
    let raf = 0, holdT = 0, startTs = 0;
    const DUR = 1700;
    const tick = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min(1, (ts - startTs) / DUR);
      setProgress(easeInOut(p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    holdT = setTimeout(() => { raf = requestAnimationFrame(tick); }, 700);
    return () => { clearTimeout(holdT); if (raf) cancelAnimationFrame(raf); };
  }, [animate, reducedMotion]);

  const youAcc  = lerp(LB_YOU_START.acc,       LB_YOU_FINAL.acc,       progress);
  const youRes  = Math.round(lerp(LB_YOU_START.res,       LB_YOU_FINAL.res,       progress));
  const youC    = Math.round(lerp(LB_YOU_START.correct,   LB_YOU_FINAL.correct,   progress));
  const youI    = Math.round(lerp(LB_YOU_START.incorrect, LB_YOU_FINAL.incorrect, progress));
  const youRank = progress >= 1 ? 3 : (progress <= 0 ? 5 : Math.round(lerp(5, 3, progress)));
  const rightP  = progress >= 1 ? 3 : (progress <= 0 ? 5 : Math.round(lerp(5, 3, progress)));
  // Row swaps trigger early in the animation window so the CSS
  // transform transition (900ms) glides through in sync with the
  // number tween finishing at progress=1.
  const swapped = progress >= 0.15;
  const rowSlot = {
    tziba:  0,
    helena: 1,
    lele:   swapped ? 3 : 2,
    adrian: swapped ? 4 : 3,
    you:    swapped ? 2 : 4,
  };
  const rowRank = {
    tziba:  1, helena: 2,
    lele:   swapped ? 4 : 3,
    adrian: swapped ? 5 : 4,
    you:    swapped ? 3 : 5,
  };

  const staticRows = [
    { key: "tziba",  name: "Tziba",       avatar: "🐢", acc: "69.23%", res: 26, ci: "18 · 8"  },
    { key: "helena", name: "Helena",      avatar: "🎭", acc: "53.57%", res: 28, ci: "15 · 13" },
    { key: "lele",   name: "LeLe FAM100", avatar: "🦁", acc: "50%",    res: 30, ci: "15 · 15" },
    { key: "adrian", name: "Adrian",      avatar: "👑", acc: "45.83%", res: 24, ci: "11 · 13" },
  ];

  return (
    <div className="hbo-scene hbo-scene--leader">
      <div className="hbo-leader-top">
        <div className="hbo-leader-trophy" aria-hidden="true">🏆</div>
        <div className="hbo-leader-hero-col">
          <div className="hbo-share-hero hbo-leader-hero" aria-hidden="true">
            <h2 className="hbo-spin-hero-line hbo-spin-hero-line--white">
              CLIMB THE <span className="hbo-leader-hero-tint">LEADERBOARD</span>
            </h2>
            <span className="hbo-spin-hero-sweep" aria-hidden="true" />
          </div>
          <p className="hbo-scene-sub">Build your record. Rise through the ranks.</p>
        </div>
      </div>
      <div className="hbo-leader-stage">
        <div className="hbo-leader-table2" data-testid="hbo-leader-table">
          <div className="hbo-leader-header2">
            <span>RANK</span>
            <span>PROPHET</span>
            <span>ACCURACY</span>
            <span>RESOLVED</span>
            <span>C · I</span>
          </div>
          <div className="hbo-leader-rows">
            {staticRows.map((r) => (
              <div key={r.key}
                   className="hbo-leader-row2"
                   style={{ "--row-slot": rowSlot[r.key] }}>
                <span className="hbo-leader-rankcol">#{rowRank[r.key]}</span>
                <span className="hbo-leader-namecol">
                  <span className="hbo-leader-avatar2" aria-hidden="true">{r.avatar}</span>
                  <span>{r.name}</span>
                </span>
                <span className="hbo-leader-acccol">
                  <FragmentDiamond size={14} /> <b>{r.acc}</b>
                </span>
                <span>{r.res}</span>
                <span className="hbo-leader-cicol">{r.ci}</span>
              </div>
            ))}
            {/* YOU row — animated stats */}
            <div className="hbo-leader-row2 is-you"
                 style={{ "--row-slot": rowSlot.you }}
                 data-testid="hbo-leader-row-you">
              <span className="hbo-leader-rankcol">#{rowRank.you}</span>
              <span className="hbo-leader-namecol">
                <span className="hbo-leader-avatar2" aria-hidden="true">🎯</span>
                <span>YOU</span>
              </span>
              <span className="hbo-leader-acccol">
                <FragmentDiamond size={14} />{" "}
                <b>{youAcc >= 51.999 ? "52%" : `${youAcc.toFixed(2)}%`}</b>
              </span>
              <span>{youRes}</span>
              <span className="hbo-leader-cicol">{youC} · {youI}</span>
            </div>
          </div>
        </div>
        <aside className="hbo-leader-side2" data-testid="hbo-leader-side">
          <div className="hbo-leader-side2-label">YOUR POSITION</div>
          <div className={`hbo-leader-side2-num ${progress > 0 && progress < 1 ? "is-changing" : ""}`}
               data-testid="hbo-leader-current-rank">
            #{rightP}
          </div>
          {progress >= 1 && (
            <div className="hbo-leader-side2-arrow" aria-hidden="true">↑</div>
          )}
        </aside>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────
 * 4 · Spend your Fragments (Marketplace)
 * ──────────────────────────────────────────────────────────────── */
const SPEND_RAFFLES = [
  { key: "basc",  img: "/img/hub/onboarding/BASC.webp",       name: "BASC #9780",       countdown: "19h 45m", ticket: 30, fp: "0.40" },
  { key: "okay",  img: "/img/hub/onboarding/Okay_Bears.webp", name: "Okay Bear #5425",  countdown: "2d 19h",  ticket: 50, fp: "1.20" },
  { key: "goats", img: "/img/hub/onboarding/GOATS.webp",      name: "GOATS #317",       countdown: "2d 16h",  ticket: 15, fp: "0.15" },
];

export function SceneSpend({ animate, reducedMotion }) {
  const [hi, setHi] = useState(0);
  useEffect(() => {
    if (!animate || reducedMotion) return;
    const id = setInterval(() => setHi((i) => (i + 1) % SPEND_RAFFLES.length),
                              1500);
    return () => clearInterval(id);
  }, [animate, reducedMotion]);

  return (
    <div className="hbo-scene hbo-scene--spend">
      <div className="hbo-spend-top">
        <div className="hbo-share-hero hbo-spend-hero" aria-hidden="true">
          <h2 className="hbo-spin-hero-line hbo-spin-hero-line--white">
            SPEND YOUR <span className="hbo-leader-hero-tint">FRAGMENTS</span>
          </h2>
          <span className="hbo-spin-hero-sweep" aria-hidden="true" />
        </div>
        <div className="hbo-spend-badge" data-testid="hbo-spend-fresh-badge">
          <FragmentDiamond size={22} />
          <span>FRESH REWARDS EVERY MONTH</span>
        </div>
      </div>
      <p className="hbo-scene-sub hbo-spend-sub">Enter raffles. Unlock rewards.</p>
      <div className="hbo-spend-cards">
        {SPEND_RAFFLES.map((r, i) => (
          <article key={r.key}
                   className={`hbo-spend-card ${i === hi ? "is-hi" : ""}`}
                   data-testid={`hbo-spend-card-${r.key}`}>
            <div className="hbo-spend-card-badges">
              <span className="hbo-spend-count">⧗ {r.countdown}</span>
              <span className="hbo-spend-tag">RAFFLE</span>
            </div>
            <div className="hbo-spend-art" role="img"
                 aria-label={r.name}
                 style={{ backgroundImage: `url("${r.img}")` }} />
            <div className="hbo-spend-name">{r.name}</div>
            <div className="hbo-spend-price">
              <FragmentDiamond size={22} />
              <span className="hbo-spend-price-num">{r.ticket}</span>
              <span className="hbo-spend-price-unit">FRG</span>
              <span className="hbo-spend-price-slash">/</span>
              <span className="hbo-spend-price-unit">TICKET</span>
            </div>
            <div className="hbo-spend-fp">FP: {r.fp} SOL</div>
            <button type="button" tabIndex={-1}
                    className="hbo-spend-btn"
                    data-testid={`hbo-spend-btn-${r.key}`}>
              ENTER RAFFLE
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 5 · Join the Adventure (Access / Connect — final scene)
 * ────────────────────────────────────────────────────────────────
 * Left column: transparent character asset with a soft cyan aura
 * behind it (both decorative, `pointer-events: none`).
 * Right column: JOIN THE / ADVENTURE headline, ACCESS REQUIRES
 * eyebrow, two outlined access blocks (Prophet NFT · OR · Valid
 * invitation) and a large CONNECT DISCORD button wired through the
 * existing `discordLoginUrl()` helper via the `onFinalCta` prop so
 * pending invitations are preserved. */
export function SceneAccess({ reducedMotion, authenticated, onFinalCta }) {
  return (
    <div className="hbo-scene hbo-scene--access2">
      <div className="hbo-access2-figure" aria-hidden="true">
        <span className={`hbo-access2-aura ${reducedMotion ? "is-static" : ""}`} />
        <img src="/img/hub/onboarding/join-adventure-character.png"
             alt=""
             className={`hbo-access2-char ${reducedMotion ? "is-static" : ""}`} />
      </div>
      <div className="hbo-access2-body">
        <div className="hbo-share-hero hbo-access2-hero" aria-hidden="true">
          <h2 className="hbo-spin-hero-line hbo-spin-hero-line--white">JOIN THE</h2>
          <h2 className="hbo-spin-hero-line hbo-spin-hero-line--tint">ADVENTURE</h2>
          <span className="hbo-spin-hero-sweep" aria-hidden="true" />
        </div>
        <div className="hbo-access2-eyebrow">ACCESS REQUIRES</div>
        <div className="hbo-access2-req">
          <div className="hbo-access2-block" data-testid="hbo-access-req-nft">
            <FragmentDiamond size={22} />
            <span>A PROPHET NFT</span>
          </div>
          <span className="hbo-access2-or">OR</span>
          <div className="hbo-access2-block" data-testid="hbo-access-req-invite">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M3 6h18v12H3z" fill="none" stroke="#c084fc" strokeWidth="1.6" />
              <path d="M3 6l9 7 9-7" fill="none" stroke="#c084fc" strokeWidth="1.6" />
            </svg>
            <span>A VALID INVITATION</span>
          </div>
        </div>
        <button type="button"
                className="hbo-access2-cta"
                onClick={onFinalCta}
                data-testid="hbo-final-cta-btn">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="hbo-access2-cta-icon">
            {/* Discord bubble — condensed path, white fill. */}
            <path fill="#ffffff" d="M19.6 4.6A18 18 0 0 0 15.3 3l-.3.6a15 15 0 0 0-5.9 0L8.7 3a18 18 0 0 0-4.3 1.6C1.9 8.2 1.3 11.7 1.6 15.1a18 18 0 0 0 5.5 2.8l1.1-1.6a12 12 0 0 1-1.8-.9l.5-.4a13 13 0 0 0 10.2 0l.5.4a12 12 0 0 1-1.8.9l1.1 1.6a18 18 0 0 0 5.5-2.8c.4-4-.6-7.5-2.8-10.5ZM9.1 13.4c-1 0-1.8-1-1.8-2.2s.8-2.2 1.8-2.2 1.8 1 1.8 2.2-.8 2.2-1.8 2.2Zm5.8 0c-1 0-1.8-1-1.8-2.2s.8-2.2 1.8-2.2 1.8 1 1.8 2.2-.8 2.2-1.8 2.2Z" />
          </svg>
          <span>{authenticated ? "ENTER THE HUB" : "CONNECT DISCORD"}</span>
        </button>
      </div>
    </div>
  );
}

